import noop from 'lodash/noop';
import { useEffect } from 'react';
import { match } from 'ts-pattern';
import { ActorRefFrom, createActor } from 'xstate';

import { type SessionSoundPlayer } from '@eluve/sounds';

import { playSoundEffects } from '../../actors';

import { transcriptionMachine } from './transcription.machine';

export type TranscriptionSoundMachineActor = ActorRefFrom<
  typeof playSoundEffects
>;

export const connectSoundsToTranscriptionMachine = ({
  machine,
  soundPlayer,
}: {
  machine: ActorRefFrom<typeof transcriptionMachine>;
  soundPlayer: SessionSoundPlayer;
}) => {
  const soundActor = createActor(playSoundEffects, {
    input: {
      soundPlayer,
    },
  });

  const soundActorSubscription = soundActor.on('*', (evt) => {
    if (evt.type === 'PLAY.DISCLAIMER.ENDED') {
      machine.send({ type: 'PLAY.DISCLAIMER.ENDED' });
    }
  });

  soundActor.start();

  const transcriptionMachineSubscription = machine.on('*', (evt) => {
    const action = match(evt)
      .with(
        { type: 'recording.started' },
        { type: 'microphone.unmuted' },
        () => () => soundActor.send({ type: 'PLAY.START' }),
      )
      .with(
        { type: 'paused' },
        () => () => soundActor.send({ type: 'PLAY.PAUSE' }),
      )
      .with(
        { type: 'stopped' },
        () => () => soundActor.send({ type: 'PLAY.STOP' }),
      )
      .with(
        { type: 'microphone.disconnected' },
        { type: 'microphone.muted' },
        { type: 'checkingUserPresence' },
        { type: 'recording.failed' },
        () => () => soundActor.send({ type: 'PLAY.ALERT' }),
      )
      .with(
        { type: 'disclaimer' },
        () => () => soundActor.send({ type: 'PLAY.DISCLAIMER' }),
      )
      .otherwise(() => noop);

    action();
  });

  return {
    cleanup: () => {
      soundActor.stop();
      soundActorSubscription.unsubscribe();
      transcriptionMachineSubscription.unsubscribe();
    },
  };
};

export const useRecordingSounds = ({
  enableSounds,
  machine,
  soundPlayer,
}: {
  enableSounds: boolean;
  machine: ActorRefFrom<typeof transcriptionMachine>;
  soundPlayer: SessionSoundPlayer;
}) => {
  useEffect(() => {
    if (!enableSounds) {
      return noop;
    }

    const { cleanup } = connectSoundsToTranscriptionMachine({
      machine,
      soundPlayer,
    });

    return () => {
      cleanup();
    };
  }, [machine, enableSounds, soundPlayer]);
};
